'use client';

import React, { useContext, useMemo, useState } from 'react';
import { Icon } from '@/components/core';
import * as styles from './toast.styles';

type Options = {
  /**
   * Time in milliseconds that the notification will be visible
   */
  time?: number;
  /**
   * If true will show a X icon to close the notification
   */
  dismissable?: boolean;
  persistent?: boolean;
};

// eslint-disable-next-line no-unused-vars
const Context = React.createContext<{ show: (children: React.ReactNode, options?: Options) => () => any }>({
  show: () => {
    return () => {};
  },
});

export const Toast: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [values, setValues] = useState<{ [id: string]: { child: React.ReactNode; options?: Options; remove: () => void } }>({});
  const value = useMemo(() => {
    return {
      show: (child: React.ReactNode, options?: Options) => {
        const id = Math.random().toString(16).substring(0, 6);
        const remove = () => {
          setValues((val) => {
            const { [id]: old, ...rest } = val;
            return rest;
          });
        };
        setValues((val) => {
          return {
            ...val,
            [id]: { child, options, remove },
          };
        });
        if (!options?.persistent) {
          setTimeout(remove, options?.time || 3000);
        }
        return remove;
      },
    };
  }, []);
  return (
    <Context.Provider value={value}>
      {children}
      <div className={styles.root}>
        {Object.entries(values).map(([id, { child, options, remove }]) => {
          return (
            <div key={id} className={styles.toast}>
              <span>{child}</span>
              {options?.dismissable && <Icon name="cross" className={styles.close} onClick={remove} />}
            </div>
          );
        })}
      </div>
    </Context.Provider>
  );
};

export const useToast = () => {
  return useContext(Context);
};
